// ── PositionSidebar — Floating position tracker popup ──────────────
// Renders as a fixed-position panel anchored to the right edge so it can
// overlay the table + detail panel without fighting either for layout
// space. The crosshair button in the toolbar opens/closes it; click
// outside or Escape dismisses.
// Reads from accountStore + marketStore via shared usePositionPnl hook.

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Crosshair, X, ExternalLink, Receipt, FoldVertical, UnfoldVertical } from 'lucide-react';
import { cn } from '@/lib/infra/utils';

const SIDEBAR_WIDTH_KEY = 'positionSidebar.width';
const SIDEBAR_WIDTH_DEFAULT = 384; // w-96
const SIDEBAR_WIDTH_MIN = 256;
const SIDEBAR_WIDTH_MAX = 800;
import { useAccountStore } from '../../stores/accountStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { usePositionPnl } from '../../hooks/ui/usePositionPnl';
import { ActivityTicker } from '../../components/ActivityTicker';
import { getAudioContext } from '../../lib/audio/audioContext';
import { describeMarket } from '../../lib/kalshiTicker';
import { ConfirmModal } from '../../components/atoms/Modal';
import { EditOrderPriceModal, OrderActionButtons } from '../../components/molecules';

// Local edit-modal target — same shape as PositionTracker's EditOrderData
// but Kalshi-only here (sidebar doesn't render Polymarket orders today).
interface SidebarEditOrder {
  orderId: string;
  marketLabel: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  remainingCount: number;
  currentPriceCents: number;
  ticker: string;
}

function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function formatCents(cents: number): string {
  return `${cents}\u00A2`;
}

export function PositionSidebar({
  onClose,
  onOpenDetail,
  variant = 'overlay',
  section = 'all',
  collapsed = false,
  onToggleCollapse,
  expanded = false,
  onToggleExpand,
}: {
  onClose: () => void;
  /** When provided, the popup title becomes a button that takes the user to
   *  the full Position Tracker tab (and closes the popup). */
  onOpenDetail?: () => void;
  /** Legacy prong collapse (header-only stat line). Superseded on the
   *  positions-page rail by the accordion expand below; kept for API
   *  compatibility with any overlay caller. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Positions-page rail accordions (Brendan, Jul 8): the card is always
   *  content-sized — never taller than its rows need. Resting
   *  (expanded=false) additionally caps the body at RESTING_CAP and
   *  scrolls it internally; expanded uncaps it (no internal scroll — the
   *  rail's stack scroller takes over). When onToggleExpand is provided
   *  the header icon becomes the expand/rest control. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** 'overlay' (default): floating panel fixed to the top-right, dismissed on
   *  click-outside / Escape — the Value Dashboard usage. 'docked': renders
   *  inline filling its parent (a sticky aside on the Positions page) as a
   *  content-pushing sidebar — no fixed positioning, resize handle, or
   *  click-outside dismiss. */
  variant?: 'overlay' | 'docked';
  /** Which slice to render. 'all' (default) = the full Value Dashboard
   *  overlay (tracker + recent fills). 'tracker' = P&L + positions + open
   *  orders only. 'fills' = recent fills only. The positions-page rail splits
   *  into two single-section prongs so each docks / pops out / hides on its
   *  own. */
  section?: 'all' | 'tracker' | 'fills';
}) {
  const status = useAccountStore((s) => s.status);
  const openOrders = useAccountStore((s) => s.openOrders);
  const balance = useAccountStore((s) => s.balance);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const replaceOrder = useAccountStore((s) => s.replaceOrder);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  // Mirror the positions-page masking (default hidden) so the trade-surface
  // sidebar can't leak the red number the stakeholder asked to hide.
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const { positionsWithPnl, totalPnl } = usePositionPnl();
  const [editOrder, setEditOrder] = useState<SidebarEditOrder | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ orderId: string; label: string } | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // Mark-to-market dollar value of all open positions. Falls back to
  // averagePrice when hydra hasn't streamed a current mark yet so the figure
  // stays populated. Mirrors the Position Tracker stat card.
  const positionsValueDollars = positionsWithPnl.reduce((sum, p) => {
    const priceCents = p.currentPrice ?? p.averagePrice;
    return sum + (priceCents * Math.abs(p.position)) / 100;
  }, 0);

  // AudioContext state indicator — poll every 2s
  const [audioState, setAudioState] = useState<string>('suspended');
  useEffect(() => {
    const check = () => setAudioState(getAudioContext()?.state ?? 'unavailable');
    check();
    const id = setInterval(check, 2000);
    return () => clearInterval(id);
  }, []);

  const executeCancel = async (orderId: string) => {
    setCancelError(null);
    try {
      await cancelOrder(orderId);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Cancel failed. Check open orders and try again.';
      setCancelError(message);
    }
  };

  const requestCancel = (orderId: string, label: string) => {
    setCancelError(null);
    if (confirmOrderCancel) {
      setPendingCancel({ orderId, label });
      return;
    }
    void executeCancel(orderId);
  };

  // Resizable width — drag left border, persist to localStorage
  const [width, setWidth] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(SIDEBAR_WIDTH_KEY) : null;
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) && n >= SIDEBAR_WIDTH_MIN && n <= SIDEBAR_WIDTH_MAX ? n : SIDEBAR_WIDTH_DEFAULT;
  });
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const onMove = (ev: PointerEvent) => {
      // Anchored on the right, so dragging left (decreasing clientX) makes it wider
      const next = Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, startWidth + (startX - ev.clientX)));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(widthRef.current));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Dismiss on Escape + click outside the popup. Skipped pointer-down on the
  // resize handle (which lives inside the popup) so resizing doesn't
  // accidentally close it.
  const popupRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The docked sidebar is persistent — only the floating overlay dismisses
    // itself on Escape / click-outside.
    if (variant !== 'overlay') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onPointerDown = (e: PointerEvent) => {
      const node = popupRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest?.('[data-order-actions-menu]')) return;
      // Toolbar toggle button has data-position-toggle so its own click (which
      // already toggles state in the parent) doesn't fire close on top of it.
      if ((e.target as HTMLElement).closest?.('[data-position-toggle]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose, variant]);

  const docked = variant === 'docked';
  const isFills = section === 'fills';
  return (
    <div
      ref={popupRef}
      className={
        docked
          ? // Content-sized, never stretched: the card ends where its rows
            // end (a tall monitor gets open rail below, not padded cards).
            // Full-bleed hard dock (Brendan, Jul 8): square corners, no
            // x-borders — the rail's border-l is the only vertical line;
            // border-y separates the card from the rail background.
            'bg-card border-y flex flex-col overflow-hidden border-gray-800'
          : 'bg-card fixed right-3 top-3 z-40 flex max-h-[calc(100vh-1.5rem)] flex-col rounded-lg border border-gray-800 shadow-2xl'
      }
      style={docked ? undefined : { width: `${width}px` }}
    >
      {/* Drag handle on the left edge — overlay only (docked width = the aside's) */}
      {!docked && (
        <div
          onPointerDown={handleResizePointerDown}
          className="absolute left-0 top-0 bottom-0 z-10 w-1 cursor-col-resize hover:bg-blue-500/40"
          title="Drag to resize"
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-800 px-3 py-2.5">
        <div className="flex items-center gap-2">
          {isFills ? (
            <>
              {onToggleExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  className={`flex items-center gap-0.5 rounded p-0.5 transition-colors hover:bg-gray-800 ${
                    expanded ? 'text-primary hover:text-primary' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  title={expanded ? 'Cap recent fills (scrolls inside)' : 'Expand recent fills to full height'}
                >
                  <Receipt size={14} />
                  {expanded ? <FoldVertical size={11} /> : <UnfoldVertical size={11} />}
                </button>
              ) : onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                  title={collapsed ? 'Expand recent fills' : 'Collapse recent fills'}
                >
                  <Receipt size={14} />
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <Receipt size={14} className="text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-300">Recent Fills</span>
            </>
          ) : (
            <>
              {onToggleExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  className={`flex items-center gap-0.5 rounded p-0.5 transition-colors hover:bg-gray-800 ${
                    expanded ? 'text-primary hover:text-primary' : 'text-gray-400 hover:text-gray-200'
                  }`}
                  title={expanded ? 'Cap the tracker (scrolls inside)' : 'Expand the tracker to full height'}
                >
                  <Crosshair size={14} />
                  {expanded ? <FoldVertical size={11} /> : <UnfoldVertical size={11} />}
                </button>
              ) : onToggleCollapse ? (
                <button
                  type="button"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  className="flex items-center gap-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"
                  title={collapsed ? 'Expand the tracker' : 'Collapse the tracker'}
                >
                  <Crosshair size={14} />
                  <ChevronDown
                    size={11}
                    className={`transition-transform ${collapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <Crosshair size={14} className="text-gray-400" />
              )}
              {onOpenDetail ? (
                <button
                  onClick={onOpenDetail}
                  className="text-sm font-medium text-gray-300 transition-colors hover:text-primary hover:underline"
                  title="Open Position Tracker"
                >
                  Positions{' '}
                  <span className="text-gray-500">
                    {positionsWithPnl.length} / ${positionsValueDollars.toFixed(2)}
                  </span>
                </button>
              ) : (
                <span className="text-sm font-medium text-gray-300">
                  Positions{' '}
                  <span className="text-gray-500">
                    {positionsWithPnl.length} / ${positionsValueDollars.toFixed(2)}
                  </span>
                </span>
              )}
              {balance !== null && (
                <span className="whitespace-nowrap text-xs text-gray-500">
                  Bal{' '}
                  <span className="text-gray-300">
                    ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </span>
              )}
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  audioState === 'running' ? 'bg-green-500' : audioState === 'suspended' ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                title={`Audio: ${audioState}`}
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-800 hover:text-gray-300"
            title={docked ? 'Hide this panel' : 'Close'}
            aria-label={docked ? 'Hide this panel' : 'Close'}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!collapsed && (
      <div
        className={
          onToggleExpand
            ? expanded
              ? // Expanded accordion: full content height, no internal
                // scroll — the rail's stack scroller handles overflow.
                ''
              : // Resting accordion: capped at a real, usable height
                // (max-h-56 = 224px ≈ 6-8 fill rows) and scrolls inside.
                // Under the cap the card just ends at its content.
                'max-h-56 overflow-y-auto'
            : 'flex-1 overflow-y-auto'
        }
      >
        {status !== 'connected' ? (
          <div className="px-3 py-6 text-center text-xs text-gray-500">
            Connect Kalshi in Settings
          </div>
        ) : (
          <>
            {section !== 'fills' && (
            <>
            {/* P&L Summary */}
            <div className="flex items-center gap-3 border-b border-gray-800/50 px-3 py-2.5">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Unrealized P&L</div>
                <div
                  className={`text-sm font-semibold ${
                    !showPnl ? 'text-gray-500' : totalPnl >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                  title={showPnl ? undefined : 'P&L hidden — toggle on the Positions page'}
                >
                  {showPnl ? formatDollars(totalPnl) : '•••'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Pos / Ord</div>
                <div className="text-sm text-gray-300">
                  {positionsWithPnl.length} / {openOrders.length}
                </div>
              </div>
            </div>

            {/* Positions List */}
            {positionsWithPnl.length > 0 && (
              <div className="border-b border-gray-800/50">
                {positionsWithPnl.map((pos) => {
                  const { marketLabel, betTeam } = describeMarket(pos.ticker, pos.side);
                  return (
                  <div
                    key={`${pos.ticker}-${pos.side}`}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                  >
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                        pos.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {betTeam ? `(${betTeam}) ` : ''}{pos.side === 'yes' ? 'Y' : 'N'}
                      </span>
                      <span className="truncate text-gray-300" title={pos.ticker}>
                        {marketLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-gray-500">{Number(pos.position.toFixed(2))}</span>
                      <span className="text-gray-500">@{formatCents(pos.averagePrice)}</span>
                      {pos.unrealizedPnl !== null ? (
                        <span className={`w-14 text-right font-mono ${
                          !showPnl ? 'text-gray-500' : pos.unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {showPnl ? formatDollars(pos.unrealizedPnl) : '•••'}
                        </span>
                      ) : (
                        <span className="w-14 text-right text-gray-600">&mdash;</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {positionsWithPnl.length === 0 && (
              <div className="border-b border-gray-800/50 px-3 py-4 text-center text-xs text-gray-500">
                No open positions
              </div>
            )}

            {/* Open Orders */}
            <div className="border-b border-gray-800/50">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500">
                Open Orders
              </div>
              {openOrders.length > 0 ? (
                openOrders.map((ord) => {
                  const { marketLabel, betTeam } = describeMarket(ord.ticker, ord.side);
                  // Kalshi often returns only yes_price even for NO orders; for
                  // a NO bid the actual price is 100 - yes_price.
                  const sidePrice =
                    ord.side === 'yes'
                      ? (ord.yesPrice ?? (ord.noPrice != null ? 100 - ord.noPrice : null))
                      : (ord.noPrice ?? (ord.yesPrice != null ? 100 - ord.yesPrice : null));
                  return (
                  <div
                    key={ord.orderId}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-800/30"
                  >
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                        ord.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {betTeam ? `(${betTeam}) ` : ''}{ord.side === 'yes' ? 'Y' : 'N'}
                      </span>
                      <span className="truncate text-gray-300" title={ord.ticker}>
                        {marketLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-gray-500">{ord.remainingCount}</span>
                      <span className="text-gray-500">
                        @{sidePrice != null ? formatCents(sidePrice) : '—'}
                      </span>
                      <span className={`rounded px-1 py-0.5 text-[9px] font-medium ${
                        ord.type === 'limit' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {ord.type === 'limit' ? 'Lim' : 'Mkt'}
                      </span>
                      <OrderActionButtons
                        onCancel={() =>
                          requestCancel(
                            ord.orderId,
                            betTeam ? `${marketLabel} (${betTeam})` : marketLabel,
                          )
                        }
                        onEdit={() => {
                          setEditOrder({
                            orderId: ord.orderId,
                            marketLabel: betTeam ? `${marketLabel} (${betTeam})` : marketLabel,
                            side: ord.side,
                            action: ord.action,
                            remainingCount: ord.remainingCount,
                            currentPriceCents: sidePrice ?? 50,
                            ticker: ord.ticker,
                          });
                        }}
                        className="ml-0.5"
                      />
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-center text-xs text-gray-600">
                  No open orders
                </div>
              )}
            </div>
            </>
            )}

            {/* Recent Fills. As its own rail prong (section 'fills') the prong
                header carries the title, so the inline sub-header + /fills
                pop-out button is only for the 'all' overlay. */}
            {section !== 'tracker' && (
              isFills ? (
                // px-1 so the fill rows' content lines up with the tracker
                // prong's px-3 rows (ActivityTicker rows are px-2; the +4px
                // wrapper matches — same as the 'all' overlay below).
                <div className="px-1 py-1">
                  <ActivityTicker />
                </div>
              ) : (
                <div className="px-1 py-1">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">
                      Recent Fills
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        // Named window so re-clicking the button focuses the
                        // existing popup instead of spawning a duplicate.
                        window.open(
                          '/fills',
                          'g1-fills-popup',
                          'width=420,height=640,toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes',
                        );
                      }}
                      className="text-gray-500 hover:bg-gray-700/50 hover:text-gray-300 rounded p-0.5 transition-colors"
                      title="Open in a separate window"
                      aria-label="Pop out recent fills"
                    >
                      <ExternalLink size={10} />
                    </button>
                  </div>
                  <ActivityTicker />
                </div>
              )
            )}
          </>
        )}
      </div>
      )}

      {cancelError && (
        <div className="mx-3 mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          <div className="flex items-start justify-between gap-2">
            <span>{cancelError}</span>
            <button
              type="button"
              onClick={() => setCancelError(null)}
              className="text-red-300/70 hover:text-red-200"
              aria-label="Dismiss cancel error"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={() => {
          if (pendingCancel) void executeCancel(pendingCancel.orderId);
        }}
        title="Cancel order?"
        message={pendingCancel ? `Cancel ${pendingCancel.label}?` : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />

      {editOrder && (
        <EditOrderPriceModal
          isOpen={editOrder !== null}
          onClose={() => setEditOrder(null)}
          onReplace={async (newPriceCents, newQuantity, options) => {
            // accountStore.replaceOrder smart-routes by default: same-price
            // qty-shrink goes through /decrease (queue-preserving); anything
            // else is cancel + new-place. The modal's force-cancel toggle
            // forwards as options.forceCancelReplace, overriding the smart
            // route when set. Sidebar only renders Kalshi orders today.
            await replaceOrder(
              editOrder.orderId,
              newPriceCents,
              newQuantity,
              options?.forceCancelReplace,
            );
            setEditOrder(null);
          }}
          marketLabel={editOrder.marketLabel}
          side={editOrder.side}
          action={editOrder.action}
          remainingCount={editOrder.remainingCount}
          currentPriceCents={editOrder.currentPriceCents}
          ticker={editOrder.ticker}
          venue="kalshi"
        />
      )}
    </div>
  );
}

// ── PositionsWidget — compact toolbar-height readout + popup toggle ──

/**
 * Lightweight P&L / counts readout that sits in the toolbar row, with the
 * crosshair as the expand control for the floating popup. Sized via
 * `self-stretch` on a flex parent so its vertical extent matches the
 * adjacent toolbar.
 */
export function PositionsWidget({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const status = useAccountStore((s) => s.status);
  const openOrders = useAccountStore((s) => s.openOrders);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  const { positionsWithPnl, totalPnl } = usePositionPnl();
  const contractCount = positionsWithPnl.reduce((sum, p) => sum + Math.abs(p.position), 0);
  // Mark-to-market dollar value across all open positions; falls back to
  // averagePrice when hydra hasn't streamed a current mark yet so the figure
  // stays populated. Mirrors the Position Tracker stat-card calculation.
  const contractsValueDollars = positionsWithPnl.reduce((sum, p) => {
    const priceCents = p.currentPrice ?? p.averagePrice;
    return sum + (priceCents * Math.abs(p.position)) / 100;
  }, 0);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const pnlClass = !showPnl
    ? 'text-muted-foreground'
    : totalPnl >= 0
      ? 'text-positive'
      : 'text-negative';

  return (
    <button
      data-position-toggle
      onClick={onToggle}
      title={expanded ? 'Hide positions' : 'Show positions'}
      aria-label={expanded ? 'Hide positions popup' : 'Show positions popup'}
      className={cn(
        'flex h-full w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
        expanded ? 'bg-primary/10' : 'hover:bg-muted/50',
      )}
    >
      {status !== 'connected' ? (
        <span className="text-muted-foreground text-xs">Not connected</span>
      ) : (
        <div className="flex flex-col justify-center gap-0.5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
              Unrealized P&amp;L
            </span>
            <span
              className={cn('font-mono text-sm font-semibold', pnlClass)}
              title={showPnl ? undefined : 'P&L hidden — toggle on the Positions page'}
            >
              {showPnl ? formatDollars(totalPnl) : '•••'}
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
              Balance
            </span>
            {balance !== null ? (
              <span className="text-foreground font-mono text-sm font-semibold">
                ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            ) : (
              <span className="text-muted-foreground font-mono text-[11px]">
                {keyMode === 'org' ? 'tracked at org level' : '—'}
              </span>
            )}
          </div>
          <div className="text-muted-foreground font-mono text-[11px]">
            <span className="text-foreground/80">{contractCount}</span> contract
            {contractCount === 1 ? '' : 's'}{' '}
            <span className="text-foreground/60">
              / ${contractsValueDollars.toFixed(2)}
            </span>
          </div>
          <div className="text-muted-foreground font-mono text-[11px]">
            <span className="text-foreground/80">{openOrders.length}</span> order
            {openOrders.length === 1 ? '' : 's'}
          </div>
        </div>
      )}
      <Crosshair
        size={16}
        className={expanded ? 'text-primary' : 'text-muted-foreground'}
      />
    </button>
  );
}
